import { bindable, transient, autoinject, computedFrom } from "aurelia-framework";
import { StepModel, ClaimsRequireTypeEnum } from "../../../../../models/basics/basics.models";
import { type VetClientModel } from "../../../../../models/vetting/vetting.models";
import { VetDetails } from "./tabs/vet-details";
import { VesselActivities } from "../../../admin/activities/vessel-activity";
import { VesselPerf } from "../../vessels/steps/safety-score-tabs/vessel-perf";
import { DocPerformance } from "../../company/steps/doc-performance/doc-performance";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { VesselLists } from "../../vessels/steps/vessel-lists";
import { VesselDetails } from "../../vessels/steps/vessel-details";
import { VesselSafetyScore } from "../../vessels/steps/vessel-safety-score";
import { VesselGhgRating } from "../../vessels/steps/vessel-ghg-rating";

@autoinject
@transient()
export class VesselDetailsDrawer {

    drawerBigger: boolean = true;
    
    @bindable
    public vet: VetClientModel;

    canViewDocAndVesselPerformance: boolean;
    canViewVesselLists: boolean;
    canAccessVesselDetails: boolean;
    showSafetyScore: boolean;
    canAccessSafetyScore: boolean;
    canAccessVesselGHGRating: boolean;

    vetChanged(newVet: VetClientModel){
        if(newVet)
            this.vesselActivity.initialize(newVet);
    }

    constructor(private vetDetails: VetDetails, private vesselActivity: VesselActivities, private vesselPerf: VesselPerf, private docPerformance: DocPerformance,
        userClaimsService: UserClaimsService, 
        private vesselLists: VesselLists,
        private vesselDetails: VesselDetails,
        private vesselSafetyScore: VesselSafetyScore,
        private vesselGhgRating: VesselGhgRating){
        this.canViewDocAndVesselPerformance = userClaimsService.hasPermission(Claims.DocAndVesselPerformanceReports);
        this.canViewVesselLists = userClaimsService.hasPermission(Claims.CanViewVesselLists);
        this.canAccessVesselDetails = userClaimsService.hasPermission(Claims.CanAccessVesselDetails);
        this.showSafetyScore = userClaimsService.hasPermission(Claims.ShowSafetyScore);
        this.canAccessSafetyScore = userClaimsService.hasPermissions([
            Claims.CanAccessSafetyScoreBenchmarking, Claims.CanAccessSafetyScoreSubscoreSummary,
            Claims.CanAccessSafetyScoreVesselScores, Claims.CanAccessSafetyScoreDocScores,
            Claims.CanAccessSafetyScoreFlagScores, Claims.CanAccessSafetyScoreClassScores], ClaimsRequireTypeEnum.RequireAny);
        this.canAccessVesselGHGRating = userClaimsService.hasPermission(Claims.CanAccessVesselGHGRating);
        this.tabs = [
            {
                title: "Overview",
                step: 1,
                show: true,
                vm: this.vetDetails,
                getModel: () => { 
                    return this.vet;
                }
            },
            {
                title: "Lists",
                show: () => this.canViewVesselLists,
                step: 2,
                vm: this.vesselLists,
                getModel: () => {                
                    return { vesselId: this.vet.vesselId };
                }
            },
            {
                lazyLoad: true,
                title: "Vessel Details",
                step: 3,
                show: () => this.canAccessVesselDetails,
                vm: this.vesselDetails,
                getModel: () => { 
                    return  { vessel: {
                        vesselId: this.vet.vesselId,
                        internationalMaritimeOrganizationNumber: this.vet.internationalMaritimeOrganizationNumber
                    }};
                }
            },
            {
                lazyLoad: true,
                title: "Safety Score",
                step: 4,
                show: () => this.showSafetyScore && this.canAccessSafetyScore,
                vm: this.vesselSafetyScore,
                getModel: () => { 
                    return { 
                        vesselId: this.vet.vesselId
                    };
                }
            },
            {
                lazyLoad: true,
                title: "GHG Rating",
                step: 5,
                show: () => this.canAccessVesselGHGRating,
                vm: this.vesselGhgRating,            
                getModel: () => { 
                    return { vessel: {
                        vesselId: this.vet.vesselId,
                        internationalMaritimeOrganizationNumber: this.vet.internationalMaritimeOrganizationNumber
                    }};
                }
            },
            {
                title: "Activities",
                step: 6,
                vm: this.vesselActivity,
                show: true,
                getModel: () => { 
                    return this.vesselActivity.initialize(this.vet);                
                }
            },
            {
                title: "Vessel Performance",
                step: 7,
                vm: this.vesselPerf,
                show: () => this.canViewDocAndVesselPerformance,
                getModel: () => {
                    return  this.vet;
                }
            },
            {
                title: "DOC Performance",
                step: 8,
                vm: this.docPerformance,
                show: () => this.canViewDocAndVesselPerformance,
                getModel: () => {
                    return {companyId: this.vet.docCompanyId};
                }
            }
        ];
    }

    @bindable
    public showVesselMoreDetailsDrawer: boolean;

    toggleVesselMoreDetailsDrawer(){
        this.showVesselMoreDetailsDrawer = !this.showVesselMoreDetailsDrawer;
    }

    toggleDrawerBigger(){
        this.drawerBigger = !this.drawerBigger;
    }


    tabChanged(event:any){
        this._currentTab = null;
        const step = event.detail.step;

        setTimeout(() => {
            const tab = this.tabs.filter(x=>x.step == step)[0];
            this._currentTab = tab
        });
    }
    

    @computedFrom("_currentTab")
    get currentTab(){
        return this._currentTab;
    }
    
    public tabs: StepModel[];

    _currentTab: StepModel;
}